import {
  AllPointOfInterestTemplateDocument,
  GetVersionsDocument,
  PointOfInterestTemplateTypeEnum,
  SortTypeEnum,
  TableCellStyleEnum,
  PointOfInterestTemplateField,
  TemplateFieldTypeEnum,
  TextFormatEnum,
  useCreatePointOfInterestTemplateMutation,
  useDeletePointOfInterestTemplateMutation,
  usePointOfInterestsCountLazyQuery,
  useUpdatePointOfInterestTemplateMutation,
  InputAdormentIconTypeEnum,
  InputAdormentPositionEnum,
  UnitsEnum,
  useGetAllPoiIconsForTemplateBuilderQuery,
} from '@/__generated__/graphql';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { DropDownField } from '@/components/shared/CustomField';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay, upperSnakeCase } from '@/utils';
import { useSnackBarMessage } from '../utils';
import { Add, AddCircle, CloudUpload, Delete, Edit } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import {
  Autocomplete,
  Checkbox,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import Button from '../../shared/Button';
import { selectedPOIType } from '../state';
import { BulkOptionsModal } from './BulkOptionsModal';
import { TemplateFields } from './shared';
import {
  DropDownContainer,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  OptionBox,
  OptionsContainer,
  dropDownStyles,
  tooltipStyles,
} from './styles';
import { PageLoader } from '../../shared/PageLoader';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { generateMongoObjectId } from '@/utils/databaseHelpers';

export const PoiTemplateForm = ({
  emptyForm,
  handleEmptyForm,
  structureId,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  structureId: string;
}) => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [selectedStaticField, setSelectedStaticField] = useState<string>();
  const [newOption, setNewOption] = useState<string>();
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<PointOfInterestTemplateField | undefined>(
    undefined
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [selectedPoiCount, setSelectedPoiCount] = useState<number>(0);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [allPoiIcons, setAllPoiIcons] = useState<string[]>([]);

  const { data: poiIconsData } = useGetAllPoiIconsForTemplateBuilderQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const allIcons = poiIconsData?.getAllPoiIcons;
    if (allIcons && allIcons?.length > 0) {
      const iconNames = allIcons.filter((icon) => icon !== null);
      setAllPoiIcons(iconNames);
    }
  }, [poiIconsData?.getAllPoiIcons]);

  const [fetchPoiCount] = usePointOfInterestsCountLazyQuery();

  useEffect(() => {
    fetchPoiCount({
      variables: {
        input: {
          structureId,
          type: selectedType?.name?.toUpperCase(),
        },
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      if (data?.pointOfInterestsCount?.count !== undefined) {
        setSelectedPoiCount(data.pointOfInterestsCount.count);
      }
    });
  }, [fetchPoiCount, selectedType?.name, structureId]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const updateField = (field: PointOfInterestTemplateField | undefined) => {
    if (!field) return;

    const updatedField = field.isMeasurementToolEnabled
      ? {
          ...field,
          type: TemplateFieldTypeEnum.MeasurementPaintLength,
          ...(field.displayConfig
            ? {
                displayConfig: {
                  ...field.displayConfig,
                  inputAdorment: {
                    position: InputAdormentPositionEnum.End,
                    iconPath: null,
                    iconType: InputAdormentIconTypeEnum.MeasurementTool,
                  },
                },
              }
            : {
                displayConfig: {
                  id: '',
                  type: TableCellStyleEnum.Text,
                  isSortable: false,
                  sortType: SortTypeEnum.String,
                  textAreaRows: undefined,
                  textFormat: TextFormatEnum.None,
                  inputAdorment: {
                    position: InputAdormentPositionEnum.End,
                    iconPath: null,
                    iconType: InputAdormentIconTypeEnum.MeasurementTool,
                  },
                },
              }),
        }
      : field;

    setSelectedField(updatedField);

    setSelectedType((previousState) => {
      if (previousState) {
        return {
          ...previousState,
          fields:
            previousState.fields && previousState.fields.length > 0
              ? previousState.fields.map((f) => (f.id === updatedField.id ? updatedField : f))
              : [updatedField],
        };
      }
    });
  };

  const handleRemoveOption = (option: string) => {
    if (selectedField) {
      const options = selectedField.options.filter((item) => item !== option) ?? [];
      updateField({ ...selectedField, options });
    }
  };

  const [updatePoiTemplate] = useUpdatePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
    onCompleted: (response) => {
      if (!response?.updatePointOfInterestTemplate) {
        showSnackBar('An error occurred while updating template', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
      if (selectedType) {
        setSelectedType({
          ...response?.updatePointOfInterestTemplate,
        });
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const [createPointOfInterestTemplate] = useCreatePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
    onCompleted: (response) => {
      if (!response?.createPointOfInterestTemplate) {
        showSnackBar('An error occurred while creating template', 'error');
        return;
      }
      showSnackBar('Template created successfully', 'success');
      if (selectedType) {
        setSelectedType({
          ...response?.createPointOfInterestTemplate,
        });
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveTemplate = useCallback(() => {
    if (selectedType?.id) {
      updatePoiTemplate({
        variables: {
          input: {
            type: selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest,
            pointOfInterestTemplateId: selectedType?.id,
            accentColor: selectedType?.accentColor,
            icon: JSON.parse(JSON.stringify(selectedType?.icon), (key, value) => {
              return key === '__typename' ? undefined : value;
            }),
            fields: selectedType?.fields
              ? selectedType?.fields.map((field) =>
                  JSON.parse(JSON.stringify(field), (key, value) => {
                    if (key === '__typename') return undefined;
                    if (key === 'key' && !value) {
                      return field.name.replace(/\s+/g, '_').toLowerCase();
                    }
                    return value;
                  })
                )
              : [],
            lastModifiedFor: 'Update POI Template',
          },
        },
      });
    }
  }, [
    selectedType?.accentColor,
    selectedType?.fields,
    selectedType?.icon,
    selectedType?.id,
    selectedType?.type,
    updatePoiTemplate,
  ]);

  const handleCreateTemplate = useCallback(() => {
    if (!selectedType?.name) {
      showSnackBar('Template name is required', 'error');
      return;
    }

    createPointOfInterestTemplate({
      variables: {
        input: {
          accentColor: selectedType?.accentColor,
          icon: JSON.parse(JSON.stringify(selectedType?.icon), (key, value) => {
            return key === '__typename' ? undefined : value;
          }),
          fields: selectedType?.fields
            ? selectedType?.fields.map((field) =>
                JSON.parse(JSON.stringify(field), (key, value) => {
                  return key === '__typename' ? undefined : value;
                })
              )
            : [],
          name: selectedType?.name ?? '',
          type: selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest,
          structureId: structureId,
          lastModifiedFor: 'Create POI Template',
        },
      },
    });
  }, [
    createPointOfInterestTemplate,
    selectedType?.accentColor,
    selectedType?.fields,
    selectedType?.icon,
    selectedType?.name,
    selectedType?.type,
    structureId,
  ]);

  const [deletePointOfInterestTemplate, { loading }] = useDeletePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument],
    onCompleted: () => {
      setIsDeleteDialogOpen(false);
      showSnackBar('POI template has been deleted successfully', 'success');
    },
    onError: (error) => {
      setIsDeleteDialogOpen(false);
      showSnackBar(`${error.message}`, 'error');
    },
  });

  const handleDeleteTemplate = useCallback(() => {
    if (selectedPoiCount > 0) {
      setIsDeleteDialogOpen(false);
    } else {
      deletePointOfInterestTemplate({
        variables: {
          templateId: selectedType?.id ?? '',
        },
      });
    }
  }, [deletePointOfInterestTemplate, selectedPoiCount, selectedType?.id]);

  const deleteDialogButtonText = useMemo(() => {
    if (selectedPoiCount > 0) {
      return 'Got it';
    }
    return loading ? 'Deleting...' : 'Delete Template';
  }, [loading, selectedPoiCount]);

  const getNameFromPath = useCallback((path: string) => {
    return path.replace(/(^.*\/)|(\.svg$)/g, '');
  }, []);

  if (!selectedType) return <PageLoader />;

  return (
    <>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title="Delete Template"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {selectedPoiCount > 0
                ? `This template has ${selectedPoiCount} POIs associated with it. Please delete all associated POIs before deleting the template.`
                : 'Are you sure you want to delete this template?'}
            </Typography>
          }
          actions={
            <>
              {selectedPoiCount === 0 && (
                <Button
                  variant="text"
                  onClick={() => setIsDeleteDialogOpen(false)}
                  sx={textButtonStyle}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => handleDeleteTemplate()}
                sx={containedButtonStyle}
                disabled={loading && selectedPoiCount === 0}
              >
                {deleteDialogButtonText}
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      {showOptionsModal && (
        <BulkOptionsModal
          open={showOptionsModal}
          handleUpdate={(newOptions) => {
            if (selectedField) {
              updateField({
                ...selectedField,
                options: [...selectedField.options, ...newOptions],
              });
            }
          }}
          handleClose={() => {
            setShowOptionsModal(false);
          }}
        />
      )}

      <Stack>
        <FormHeader>
          {emptyForm ? (
            <TemplateFields
              value={''}
              placeholder="Template Name"
              mode={emptyForm ? 'Edit' : mode}
              onChange={(value) => {
                setSelectedType({ ...selectedType, name: value });
              }}
            />
          ) : (
            <Typography fontSize="2.2rem" fontWeight={500}>
              {selectedType.name}
            </Typography>
          )}

          {mode === 'Edit' || emptyForm ? (
            <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => {
                  if (emptyForm) {
                    handleEmptyForm(false);
                    handleCreateTemplate();
                  } else {
                    setMode('ReadOnly');
                    handleSaveTemplate();
                  }
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => {
                  setMode('ReadOnly');
                  handleEmptyForm(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
              <Tooltip
                title="Edit Template"
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { tooltipStyles },
                  },
                }}
              >
                <IconButton size="medium" onClick={() => setMode('Edit')}>
                  <Edit
                    fontSize="inherit"
                    sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip
                title="Delete Template"
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { tooltipStyles },
                  },
                }}
              >
                <IconButton size="medium" onClick={() => setIsDeleteDialogOpen(true)}>
                  <Delete
                    fontSize="inherit"
                    sx={{ cursor: 'pointer', color: abyssColors.error[700] }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </FormHeader>

        <LineBreak marginBottom={2} />
        <Stack direction="row" width="100%" gap={2}>
          <FieldsList>
            <Stack gap={1}>
              <Field
                selected={selectedStaticField === 'Template Type'}
                onClick={() => {
                  setSelectedStaticField('Template Type');
                  setSelectedField(undefined);
                }}
              >
                Template Type
              </Field>
              <Field
                selected={selectedStaticField === 'Icon'}
                onClick={() => {
                  setSelectedStaticField('Icon');
                  setSelectedField(undefined);
                }}
              >
                Icon
              </Field>
              <Field
                selected={selectedStaticField === 'Legend Color'}
                onClick={() => {
                  setSelectedStaticField('Legend Color');
                  setSelectedField(undefined);
                  setShowColorPicker(!showColorPicker);
                }}
              >
                Legend Color
              </Field>
              {selectedType.fields?.map((field) => (
                <Field
                  selected={selectedField?.id === field.id}
                  key={field.id}
                  onClick={() => {
                    setSelectedStaticField(undefined);
                    setSelectedField(
                      field.displayConfig
                        ? field
                        : {
                            ...field,
                            // inserting dummy object for saving values, should not make a difference
                            displayConfig: undefined,
                          }
                    );
                  }}
                >
                  {field.name}
                </Field>
              ))}
            </Stack>
            {(mode === 'Edit' || emptyForm) && (
              <Button
                sx={{ marginTop: 2, height: '40px' }}
                startIcon={<Add />}
                variant="outlined"
                size="small"
                onClick={() => {
                  const newField = {
                    name: '',
                    key: '',
                    options: [],
                    type: TemplateFieldTypeEnum.Input,
                    isReadOnly: true,
                    isRequired: true,
                    serverSide: true,
                    isMeasurementToolEnabled: false,
                    id: generateMongoObjectId(),
                    displayConfig: {
                      id: generateMongoObjectId(),
                      type: TableCellStyleEnum.Text,
                      isSortable: false,
                      sortType: SortTypeEnum.String,
                      textAreaRows: undefined,
                      textFormat: TextFormatEnum.None,
                    },
                  };
                  setSelectedType({
                    ...selectedType,
                    fields:
                      selectedType.fields && selectedType.fields?.length > 0
                        ? [...selectedType.fields, newField]
                        : [newField],
                  });
                  setSelectedField(newField);
                }}
              >
                Add Field
              </Button>
            )}
          </FieldsList>
          {!selectedField && selectedStaticField && (
            <FieldPanel>
              <Typography fontWeight={500} fontSize="1.8rem">
                {selectedStaticField}
              </Typography>
              {selectedStaticField === 'Template Type' && (
                <DropDownContainer sx={{ pt: 2 }}>
                  <Typography>Template Type</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedStaticField}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest}
                    options={Object.values(PointOfInterestTemplateTypeEnum).map((value) => value)}
                    onFieldValueChange={(value) => {
                      if (value) {
                        setSelectedType({
                          ...selectedType,
                          type: value as PointOfInterestTemplateTypeEnum,
                        });
                      }
                    }}
                  />
                </DropDownContainer>
              )}
              {selectedStaticField === 'Icon' && (
                <Stack pt={2}>
                  <Stack direction="row" gap={0}>
                    <DropDownContainer>
                      <Typography>Icon</Typography>
                      <Autocomplete
                        id={selectedType?.icon?.name}
                        disableClearable
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        options={allPoiIcons.map((path) => (path ? getNameFromPath(path) : ''))}
                        value={
                          selectedType?.icon?.name
                            ? getNameFromPath(selectedType.icon.name)
                            : NOT_APPLICABLE_LABEL
                        }
                        onChange={(_, value) => {
                          if (value) {
                            setSelectedType((prev) => {
                              if (!prev) return prev;
                              return {
                                ...prev,
                                icon: {
                                  ...(prev.icon ?? {}),
                                  name: `common/poi-icons/${value}.svg`,
                                },
                              };
                            });
                          }
                        }}
                        getOptionLabel={(option) => option}
                        sx={{
                          ...dropDownStyles,
                          width: '100%',
                        }}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth variant="standard" placeholder="" />
                        )}
                      />
                    </DropDownContainer>
                    {selectedType.icon?.name && selectedType.icon?.name !== '' && (
                      <img
                        src={getCloudfrontUrl(selectedType?.icon?.name)}
                        crossOrigin="use-credentials"
                        alt="poi-icon"
                      />
                    )}
                    {selectedType.icon?.name === '' && <AbyssIcon iconName="unknown" />}
                  </Stack>
                  <TemplateFields
                    label="POI Color"
                    value={selectedType?.icon?.color ?? NOT_APPLICABLE_LABEL}
                    endAdornment="color"
                    onChange={(value) => {
                      if (value) {
                        setSelectedType((previousState) => {
                          if (previousState) {
                            return {
                              ...previousState,
                              ...(previousState.icon
                                ? { icon: { ...previousState.icon, color: value.toUpperCase() } }
                                : { icon: { name: '', color: '' } }),
                            };
                          }
                        });
                      }
                    }}
                    mode={emptyForm ? 'Edit' : mode}
                  />
                </Stack>
              )}
              {selectedStaticField === 'Legend Color' && (
                <Stack pt={2}>
                  <TemplateFields
                    label="Color"
                    value={selectedType?.accentColor ?? '#8FCE00'}
                    onChange={(value) => {
                      if (value) {
                        setSelectedType({ ...selectedType, accentColor: value.toUpperCase() });
                      }
                    }}
                    endAdornment="color"
                    mode={emptyForm ? 'Edit' : mode}
                  />
                </Stack>
              )}
            </FieldPanel>
          )}
          {selectedField ? (
            <FieldPanel gap={3} direction="row">
              <Stack width={'50%'} gap={1}>
                {mode === 'ReadOnly' && (
                  <Typography fontWeight={500} fontSize="1.8rem">
                    {selectedField.name}
                  </Typography>
                )}

                <Grid container maxWidth="60rem" ml={'-1.5rem'}>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.isRequired ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({
                            ...selectedField,
                            isRequired: event.target.checked,
                          });
                        }}
                      />
                      <Typography>Required</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Tooltip
                        title="Field Type will be updated to 'MEASUREMENT_PAINT_LENGTH' to enable Measurement Tool"
                        arrow
                        placement="top"
                        sx={{ tooltipStyles }}
                      >
                        <span>
                          <Checkbox
                            checked={selectedField.isMeasurementToolEnabled ?? false}
                            disabled={mode === 'ReadOnly' && !emptyForm}
                            onChange={(event) => {
                              updateField({
                                ...selectedField,
                                isMeasurementToolEnabled: event.target.checked,
                              });
                            }}
                          />
                        </span>
                      </Tooltip>
                      <Typography>Enable Measurement Tool</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.defaultVisible ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, defaultVisible: event.target.checked });
                        }}
                      />
                      <Typography>Default Visibility</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.isHidden ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, isHidden: event.target.checked });
                        }}
                      />
                      <Typography>Hidden</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.isReadOnly ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, isReadOnly: event.target.checked });
                        }}
                      />
                      <Typography>Read Only</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.serverSide}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, serverSide: event.target.checked });
                        }}
                      />
                      <Typography>Server-side</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.displayConfig?.isSortable}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({
                            ...selectedField,
                            ...(selectedField.displayConfig
                              ? {
                                  displayConfig: {
                                    ...selectedField.displayConfig,
                                    isSortable: event.target.checked,
                                  },
                                }
                              : {
                                  displayConfig: {
                                    id: generateMongoObjectId(),
                                    type: TableCellStyleEnum.Text,
                                    isSortable: event.target.checked,
                                    sortType: SortTypeEnum.String,
                                    textAreaRows: undefined,
                                    textFormat: TextFormatEnum.None,
                                  },
                                }),
                          });
                        }}
                      />
                      <Typography>Sortable</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.displayInReview ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, displayInReview: event.target.checked });
                        }}
                      />
                      <Typography>Display in Review</Typography>
                    </Stack>
                  </Grid>
                </Grid>
                {(mode === 'Edit' || emptyForm) && (
                  <TemplateFields
                    label="Name"
                    value={selectedField.name}
                    mode={emptyForm ? 'Edit' : mode}
                    onChange={(value) => {
                      updateField({
                        ...selectedField,
                        name: value,
                      });
                    }}
                  />
                )}
                <DropDownContainer>
                  <Typography>Field Type</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={
                      selectedField.isMeasurementToolEnabled
                        ? formatEnumForDisplay(TemplateFieldTypeEnum.MeasurementPaintLength)
                        : formatEnumForDisplay(selectedField.type) || ''
                    }
                    options={Object.values(TemplateFieldTypeEnum).map((value) =>
                      formatEnumForDisplay(value)
                    )}
                    onFieldValueChange={(value) => {
                      if (value && selectedField) {
                        const type =
                          Object.values(TemplateFieldTypeEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          ) ?? TemplateFieldTypeEnum.Input;
                        updateField({
                          ...selectedField,
                          type: type,
                        });
                      }
                    }}
                  />
                </DropDownContainer>
                {selectedField.displayConfig?.isSortable && (
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    width="100%"
                    maxWidth="50rem"
                    justifyContent="space-between"
                  >
                    <Typography>Sort Type</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.displayConfig.sortType) || ''}
                      options={Object.values(SortTypeEnum).map((value) =>
                        formatEnumForDisplay(value)
                      )}
                      onFieldValueChange={(value) => {
                        if (value) {
                          const sortType = Object.values(SortTypeEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          );
                          updateField({
                            ...selectedField,
                            ...(selectedField.displayConfig
                              ? {
                                  displayConfig: {
                                    ...selectedField.displayConfig,
                                    ...(sortType
                                      ? { sortType }
                                      : { sortType: SortTypeEnum.String }),
                                  },
                                }
                              : {
                                  displayConfig: {
                                    id: generateMongoObjectId(),
                                    type: TableCellStyleEnum.Text,
                                    isSortable: false,
                                    ...(sortType
                                      ? { sortType }
                                      : { sortType: SortTypeEnum.String }),
                                    textAreaRows: undefined,
                                  },
                                }),
                          });
                        }
                      }}
                    />
                  </Stack>
                )}

                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width="100%"
                  maxWidth="50rem"
                  justifyContent="space-between"
                >
                  <Typography>Text Format</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={formatEnumForDisplay(selectedField.displayConfig?.textFormat) || ''}
                    options={Object.values(TextFormatEnum).map((value) =>
                      formatEnumForDisplay(value)
                    )}
                    onFieldValueChange={(value) => {
                      if (value) {
                        const textFormat = Object.values(TextFormatEnum).find(
                          (enumValue) => enumValue === upperSnakeCase(value)
                        );
                        updateField({
                          ...selectedField,
                          ...(selectedField.displayConfig
                            ? {
                                displayConfig: {
                                  ...selectedField.displayConfig,
                                  ...(textFormat
                                    ? { textFormat }
                                    : { textFormat: TextFormatEnum.None }),
                                },
                              }
                            : {
                                displayConfig: {
                                  id: generateMongoObjectId(),
                                  type: TableCellStyleEnum.Text,
                                  isSortable: false,
                                  sortType: SortTypeEnum.String,
                                  ...(textFormat
                                    ? { textFormat }
                                    : { textFormat: TextFormatEnum.None }),
                                  textAreaRows: undefined,
                                },
                              }),
                        });
                      }
                    }}
                  />
                </Stack>

                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width="100%"
                  maxWidth="50rem"
                  justifyContent="space-between"
                >
                  <Typography>Unit</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={formatEnumForDisplay(selectedField.unit) || ''}
                    options={Object.values(UnitsEnum).map((value) => formatEnumForDisplay(value))}
                    onFieldValueChange={(value) => {
                      if (value) {
                        const unit = Object.values(UnitsEnum).find(
                          (enumValue) => enumValue === upperSnakeCase(value)
                        );
                        updateField({
                          ...selectedField,
                          unit,
                        });
                      }
                    }}
                  />
                </Stack>

                {selectedField.type === TemplateFieldTypeEnum.Textarea && (
                  <TemplateFields
                    label="Text Area Rows"
                    value={selectedField.displayConfig?.textAreaRows?.toString()}
                    mode={emptyForm ? 'Edit' : mode}
                    type={TemplateFieldTypeEnum.Numeric}
                  />
                )}
                <DropDownContainer>
                  <Typography>Display Type</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={formatEnumForDisplay(selectedField.displayConfig?.type) || ''}
                    options={Object.values(TableCellStyleEnum).map((value) =>
                      formatEnumForDisplay(value)
                    )}
                    onFieldValueChange={(value) => {
                      if (value) {
                        const type = Object.values(TableCellStyleEnum).find(
                          (enumValue) => enumValue === upperSnakeCase(value)
                        );
                        updateField({
                          ...selectedField,
                          ...(selectedField.displayConfig
                            ? {
                                displayConfig: {
                                  ...selectedField.displayConfig,
                                  ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                },
                              }
                            : {
                                displayConfig: {
                                  id: generateMongoObjectId(),
                                  ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                  isSortable: false,
                                  sortType: SortTypeEnum.String,
                                  inputAdorment: undefined,
                                  textAreaRows: undefined,
                                  textFormat: TextFormatEnum.None,
                                },
                              }),
                        });
                      }
                    }}
                  />
                </DropDownContainer>

                <LineBreak marginY={4} />
                {(mode === 'Edit' || emptyForm) && (
                  <Button
                    sx={{ width: '50%', height: '40px' }}
                    startIcon={<Delete />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (selectedType.fields) {
                        setSelectedType({
                          ...selectedType,
                          fields: selectedType.fields.filter(
                            (field) => field.id !== selectedField.id
                          ),
                        });
                        setSelectedField(undefined);
                      }
                    }}
                  >
                    Remove Field
                  </Button>
                )}
              </Stack>
              {(selectedField.type === TemplateFieldTypeEnum.MultiSelect ||
                selectedField.type === TemplateFieldTypeEnum.SingleSelect ||
                selectedField.type === TemplateFieldTypeEnum.RadioButton) && (
                <Stack sx={{ width: '50%' }} gap={2}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography fontWeight={500} fontSize="1.6rem">
                      Options
                    </Typography>
                    <Typography variant="subtitle2">({selectedField.options.length})</Typography>
                  </Stack>
                  <OptionsContainer>
                    {selectedField.options.map((option, index) => (
                      <OptionBox key={`${option}-${index}`}>
                        <Typography>{option}</Typography>
                        {(mode === 'Edit' || emptyForm) && (
                          <Delete
                            onClick={() => {
                              handleRemoveOption(option);
                            }}
                          />
                        )}
                      </OptionBox>
                    ))}
                  </OptionsContainer>
                  {(mode === 'Edit' || emptyForm) && (
                    <>
                      <Stack direction="row" gap={2} marginTop={2}>
                        <TemplateFields
                          label="Field option"
                          value={newOption}
                          onChange={(value) => {
                            if (value) {
                              setNewOption(value);
                            }
                          }}
                          mode={emptyForm ? 'Edit' : mode}
                        />
                        <Button
                          sx={{ width: '50%', height: '40px' }}
                          startIcon={<AddCircle />}
                          variant="outlined"
                          size="small"
                          disabled={!newOption}
                          onClick={() => {
                            if (newOption) {
                              updateField({
                                ...selectedField,
                                options: [...selectedField.options, newOption],
                              });
                            }
                            setNewOption('');
                          }}
                        >
                          Add New Option
                        </Button>
                      </Stack>
                      <Button
                        sx={{ width: '50%', height: '40px', ml: 'auto' }}
                        startIcon={<CloudUpload />}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setShowOptionsModal(true);
                        }}
                      >
                        Add options from file
                      </Button>
                    </>
                  )}
                </Stack>
              )}
            </FieldPanel>
          ) : (
            !selectedStaticField && (
              <FieldPanel justifyContent="center" alignItems="center">
                <EmptyStatePlaceholder
                  type="poiTable"
                  message="Click on any field in the left panel to see its information"
                />
              </FieldPanel>
            )
          )}
        </Stack>
      </Stack>
    </>
  );
};
